/**
 * Validation for an edited exam format — pure, and deliberately the same eight rules
 * `content.profile_routes.validate` enforces, so a bad edit is refused before the round trip.
 */
import type { ProfilePut } from "../api/api";

export const KNOWN_TYPES = [
  "multiple_choice",
  "short_answer",
  "extended",
  "code_trace",
  "calculation",
] as const;

export interface Issue {
  field: keyof ProfilePut;
  message: string;
}

export function validate(body: ProfilePut): Issue[] {
  const issues: Issue[] = [];
  if (!body.command_words.some((w) => w.trim())) {
    issues.push({ field: "command_words", message: "Keep at least one command word." });
  }
  if (body.total_marks <= 0) {
    issues.push({ field: "total_marks", message: "Total marks must be greater than zero." });
  }
  if (body.duration_minutes <= 0) {
    issues.push({ field: "duration_minutes", message: "Duration must be greater than zero." });
  }
  const share = body.question_types.reduce((s, q) => s + q.share, 0);
  if (body.question_types.length > 0 && Math.abs(share - 1) > 0.05) {
    issues.push({
      field: "question_types",
      message: `Question type shares add up to ${share.toFixed(2)}, not 1.`,
    });
  }
  if (body.question_types.some((q) => !(KNOWN_TYPES as readonly string[]).includes(q.type))) {
    issues.push({
      field: "question_types",
      message: `Question types must be one of ${KNOWN_TYPES.join(", ")}.`,
    });
  }
  if (body.question_types.some((q) => q.typical_marks <= 0)) {
    issues.push({ field: "question_types", message: "Typical marks must be greater than zero." });
  }
  // Checked once the shares are near 1 and the total is valid, so one edit reports one thing.
  // Math.round is half-up, which the server's _half_up matches.
  const marks = body.question_types.reduce((n, q) => n + Math.round(q.share * body.total_marks), 0);
  const sane =
    body.question_types.length > 0 && body.total_marks > 0 && Math.abs(share - 1) <= 0.05;
  if (sane && marks !== body.total_marks) {
    issues.push({
      field: "question_types",
      message: `Question types add up to ${marks} marks, not ${body.total_marks}.`,
    });
  }
  const r = body.per_question_range;
  if (r !== undefined) {
    const [lo, hi] = r;
    const bad =
      r.length !== 2 ||
      lo === undefined ||
      hi === undefined ||
      !(1 <= lo && lo <= hi) ||
      (body.total_marks > 0 && hi > body.total_marks);
    if (bad) {
      issues.push({
        field: "per_question_range",
        message:
          "The range is two numbers: 1 or more, lowest first, highest no more than the total.",
      });
    }
  }
  return issues;
}
